import {GeojsonLayer} from '@apps-common/ui-maps';
import FeaturesMap from '@apps-common/ui-maps/FeaturesMap.jsx';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import {
  Alert,
  Box,
  Button,
  Card,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
  useColorScheme,
} from '@mui/material';
import {pink} from '@mui/material/colors';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import FeatureCardContent from './FeatureCardContent.jsx';
import {notify} from '@actions/notifications.js';
import {
  useApproveFeature,
  useGetUploadReviewSingleFeature,
} from '@api/asyncIngest.js';
import {trackEvent} from '@app/analytics.js';
import {APPROVAL_OUTCOMES, FEATURE_STATE} from '@app/constants/uploads';

import ErrorMessage from '@components/common/ErrorMessage.jsx';

const ReviewFeatures = ({upload, features, viewInvalid, setViewInvalid}) => {
  const dispatch = useDispatch();
  const {colorScheme} = useColorScheme();

  const initialFeatureIndex = features.findIndex(
    feature => feature.state === FEATURE_STATE.ALTERNATE
  );

  const [currentFeature, setCurrentFeature] = useState(
    initialFeatureIndex >= 0 ? initialFeatureIndex : 0
  );

  const feature = useMemo(() => {
    return features[currentFeature];
  }, [features, currentFeature]);

  const disablePrev = useMemo(() => currentFeature === 0, [currentFeature]);
  const disableNext = useMemo(
    () => currentFeature === features.length - 1,
    [currentFeature, features]
  );

  // move to the next feature, without going out of bounds
  const nextFeature = () => {
    if (currentFeature < features.length - 1) {
      setCurrentFeature(currentFeature + 1);
    }
    trackEvent('Async Upload', {
      action: 'clicked next feature',
      uploadId: upload.id,
    });
  };

  // move to the previous feature, without going below 0.
  const prevFeature = () => {
    if (currentFeature > 0) {
      setCurrentFeature(currentFeature - 1);
    }
    trackEvent('Async Upload', {
      action: 'clicked previous feature',
      uploadId: upload.id,
    });
  };

  const {
    data: featureData,
    error: featureError,
    isPending,
  } = useGetUploadReviewSingleFeature({upload, feature}, {enabled: !!feature});

  // a "feature" in the context of this component is not a GeoJSON feature,
  // but metadata about a feature in the API. Since we need a GeoJSON feature
  // to use with the map, we need to create one using either the original or
  // alternate geometry.
  const geojsonFeature = feat => {
    return {
      type: 'Feature',
      geometry: feat,
    };
  };

  const onApproveFeatureError = () => {
    dispatch(
      notify({
        message: 'There was an error reviewing the feature. Please try again.',
        severity: 'error',
      })
    );
  };

  const {mutate: approve, isPending: approveIsPending} = useApproveFeature(
    {upload, feature},
    {
      onSuccess: nextFeature,
      onSettled: (data, error, outcome) => {
        trackEvent('Async Upload', {
          action: 'review feature',
          approvalOutcome: outcome,
          uploadId: upload.id,
        });
      },
      onError: onApproveFeatureError,
    }
  );

  return (
    <Stack
      sx={{
        marginBlock: 2,
        paddingInline: 3,
        height: '100%',
        gap: 2,
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: 1,
          alignItems: 'center',
          flexShrink: 0,
          justifyContent: 'center',
        }}
      >
        <Button
          color="primary"
          onClick={prevFeature}
          aria-label="previous feature"
          disabled={disablePrev}
          size="small"
          variant="outlined"
          sx={{minWidth: 32, width: 32}}
        >
          <ArrowBackIcon fontSize="small" />
        </Button>
        <Typography variant="body2">
          {currentFeature + 1} of {features.length}
        </Typography>
        <Button
          color="primary"
          onClick={nextFeature}
          disabled={disableNext}
          aria-label="next feature"
          size="small"
          variant="outlined"
          sx={{minWidth: 32, width: 32}}
        >
          <ArrowForwardIcon fontSize="small" />
        </Button>
      </Stack>

      <Stack
        direction={{sm: 'column', md: 'row'}}
        sx={{width: '100%', height: '100%', gap: 2}}
      >
        <Stack
          sx={theme => ({
            width: '50%',
            [theme.breakpoints.down('md')]: {
              width: '100%',
            },
          })}
        >
          <Box sx={{minHeight: 300, height: '100%'}}>
            <FeaturesMap
              features={
                featureData ? [geojsonFeature(featureData.geometry)] : []
              }
              animationDuration={0}
            >
              <GeojsonLayer
                features={
                  featureData?.alternative
                    ? [geojsonFeature(featureData.alternative)]
                    : []
                }
                style={[
                  {
                    'stroke-width': 5,
                    'stroke-color': `${pink[300]}`,
                  },
                  {
                    'stroke-width': 3,
                    'stroke-color': `${pink[500]}`,
                  },
                ]}
              />
            </FeaturesMap>
          </Box>
        </Stack>
        <Stack
          sx={theme => ({
            width: '50%',
            [theme.breakpoints.down('md')]: {
              width: '100%',
            },
          })}
        >
          {featureError ? (
            <ErrorMessage message={featureError} />
          ) : (
            <Stack sx={{paddingInlineStart: 2, paddingInlineEnd: 3, gap: 2}}>
              <Card>
                <FeatureCardContent
                  featureData={featureData}
                  isPending={isPending}
                />
              </Card>
              {[FEATURE_STATE.APPROVED, FEATURE_STATE.DISCARDED].includes(
                featureData?.state
              ) && (
                <Alert
                  severity={
                    featureData?.state === FEATURE_STATE.APPROVED
                      ? 'info'
                      : 'error'
                  }
                  iconMapping={{
                    info: <ThumbUpOffAltIcon />,
                    error: <ThumbDownOffAltIcon />,
                  }}
                  variant={colorScheme === 'dark' ? 'outlined' : 'standard'}
                >
                  {featureData?.state === FEATURE_STATE.APPROVED
                    ? 'Simplification accepted. This feature will be imported.'
                    : 'Simplification rejected. This feature will be skipped.'}
                </Alert>
              )}

              {!isPending && !featureData.state === FEATURE_STATE.ERROR && (
                <Stack
                  direction="row"
                  sx={{gap: 1, justifyContent: 'flex-end'}}
                >
                  <Button
                    color="secondary"
                    onClick={() => approve(APPROVAL_OUTCOMES.DISCARD)}
                    disabled={
                      approveIsPending ||
                      featureData.state === FEATURE_STATE.DISCARDED
                    }
                    variant="outlined"
                    startIcon={<ThumbDownOffAltIcon />}
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => approve(APPROVAL_OUTCOMES.ACCEPT)}
                    color="primary"
                    variant="outlined"
                    endIcon={<ThumbUpOffAltIcon />}
                    disabled={
                      approveIsPending ||
                      featureData.state === FEATURE_STATE.APPROVED
                    }
                  >
                    Approve
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
      {upload.total_errors > 0 && (
        <div>
          <FormControlLabel
            label={`Show ${upload.total_errors} invalid feature${upload.total_errors > 1 ? 's' : ''} (not imported)`}
            control={
              <Switch
                size="small"
                checked={viewInvalid}
                onChange={() => setViewInvalid(v => !v)}
              />
            }
            slotProps={{
              typography: {
                variant: 'body2',
              },
            }}
            sx={{
              marginLeft: 0,
            }}
          />
        </div>
      )}
    </Stack>
  );
};

ReviewFeatures.propTypes = {
  upload: PropTypes.object,
  features: PropTypes.arrayOf(PropTypes.object),
  viewInvalid: PropTypes.bool,
  setViewInvalid: PropTypes.func,
};

export default ReviewFeatures;
